import { type JSX, memo, useMemo } from 'react';

import type { OddsFormat } from '@cx-modules/types';

import { formatOddsText } from '../../helpers/format-odds';
import type { Bet, Odd } from '../../types';
import { BetRowLeft } from '../BetRowLeft';
import { ExchangeOddsCell, SportsbookOddsCell } from '../OddsCell';

import { BetNameColumn, BetRow } from './style';

interface OddsGridBetRowProps {
  bet: Bet;
  betsWithBestOdds: (Bet & { bestOdds: number; bestOddsFractional: string; bestOddsText: string | number })[];
  exchangeIds: string[];
  marketId: number;
  marketName: string;
  oddsFormat: OddsFormat;
  showHistory: boolean;
  sportsbookIds: string[];
}

const OddsGridBetRowComponent = ({
  bet,
  betsWithBestOdds,
  exchangeIds,
  marketId,
  marketName,
  oddsFormat,
  showHistory,
  sportsbookIds,
}: OddsGridBetRowProps): JSX.Element => {
  const betWithBestOdds = betsWithBestOdds.find(b => b.id === bet.id);
  const bestOddsValue = betWithBestOdds?.bestOdds;

  const bestBookieCodes = useMemo(() => {
    if (!betWithBestOdds || !bestOddsValue) return new Set<string>();
    return new Set(
      betWithBestOdds.odds.filter((odd: Odd) => odd.decimal === bestOddsValue).map(odd => odd.bookmakerCode),
    );
  }, [betWithBestOdds, bestOddsValue]);

  const oddsDataMap = useMemo(() => {
    const map = new Map<string, { odds: Odd | undefined; oddsText: string | number | undefined }>();

    bet.odds.forEach(odd => {
      map.set(odd.bookmakerCode, {
        odds: odd,
        oddsText: formatOddsText(odd, oddsFormat),
      });
    });

    return map;
  }, [bet.odds, oddsFormat]);

  return (
    <BetRow>
      <BetNameColumn>
        <BetRowLeft bet={bet} marketId={marketId} marketName={marketName} showHistory={showHistory} />
      </BetNameColumn>
      <div />
      {sportsbookIds.map((bookieCode: string) => {
        const oddsData = oddsDataMap.get(bookieCode);
        return (
          <SportsbookOddsCell
            bet={bet}
            bookieCode={bookieCode}
            isBest={bestBookieCodes.has(bookieCode)}
            key={`${bet.id}_${bookieCode}`}
            odds={oddsData?.odds}
            oddsFormat={oddsFormat}
            oddsText={oddsData?.oddsText}
          />
        );
      })}
      {exchangeIds.length > 0 && <div />}
      {exchangeIds.map((bookieCode: string) => {
        const oddsData = oddsDataMap.get(bookieCode);
        return (
          <ExchangeOddsCell
            bet={bet}
            bookieCode={bookieCode}
            isBest={bestBookieCodes.has(bookieCode)}
            key={`${bet.id}_${bookieCode}`}
            odds={oddsData?.odds}
            oddsFormat={oddsFormat}
            oddsText={oddsData?.oddsText}
          />
        );
      })}
    </BetRow>
  );
};

export const OddsGridBetRow = memo(OddsGridBetRowComponent);
